/* The plain words the Wi-Fi Networks screen says. The box's own setup page
 * (cloud-anchor/pkg/setupui) scans and joins; this screen asks it the same
 * questions from afar, so every sentence here describes what the box
 * answered, never what was asked of it. */

export const READ_ONLY = "Your account can see the networks but not join or leave them.";

export const NEEDS_UPDATE_FEATURE = "Joining a Wi-Fi network from this console";

export const NEEDS_UPDATE =
  "The box runs software from before joining was added, so it shows the networks it can see but cannot join " +
  "or leave one from here. Join appears on this screen once the box is updated; until then, use the box's own " +
  "setup page from a device on its network.";

export const NO_MANAGER =
  "This box does not manage Wi-Fi itself, so there are no networks to show or join from here.";

export const NO_RADIO = "This box has no Wi-Fi radio.";

export const CAPTIVE =
  "This network wants a sign-in page before it carries anything, and the box cannot open one. Ask whoever runs " +
  "it to let the box in, or join another network.";

/* nmcli reports signal as a percentage; people read it as a word. */
export function signalWord(pct) {
  if (pct >= 75) return "strong";
  if (pct >= 55) return "good";
  if (pct >= 35) return "fair";
  return "weak";
}

/* How many of four bars to light, the way a phone shows it. */
export const bars = (pct) => (pct >= 75 ? 4 : pct >= 55 ? 3 : pct >= 35 ? 2 : pct > 0 ? 1 : 0);

export const isOpen = (security) => !security || security === "--";

/* A company network (802.1X) wants an account, not a password, which a join
   by password can never give it. */
export const isCompany = (security) => /802\.1X/i.test(security || "");

export function band(mhz) {
  if (!mhz) return "";
  if (mhz >= 5925) return "6 GHz";
  return mhz >= 5000 ? "5 GHz" : "2.4 GHz";
}

/* The lines this box has up, from /wans, and whether its Wi-Fi is among them. */
export function lines(status, wans) {
  const up = (wans || []).filter((w) => w.state === "up");
  return { count: up.length, wifi: !!status?.device && up.some((w) => w.name === status.device) };
}

/* Whether leaving the connected network takes the box's only line. Unknown
   (no /wans answer) claims nothing either way. */
export function onlyLine(status, wans) {
  if (!status?.connected || !wans) return false;
  const l = lines(status, wans);
  return l.wifi && l.count === 1;
}

/* What leaving the only line up costs, and until when, which depends on how
   the box leaves it. */
export function onlyLineWords(ssid, until, singleEdge) {
  const reach = singleEdge ? "" : ", and out of this console's reach,";
  return `${ssid} is the only internet line this box has up. Leaving it takes the box off the internet${reach} ${until}.`;
}

/* The answer the screen opens with. Green only for a network carrying the
   box's traffic; one that wants a sign-in page is amber. */
export function headline(status, wans, captive) {
  if (status.join?.joining) return { tone: "off", text: `Joining ${status.join.ssid}…` };
  if (!status.device && !status.connected) return { tone: "off", text: NO_RADIO };
  if (!status.connected || !status.ssid) return { tone: "off", text: "Not connected to any Wi-Fi network." };
  if (captive) return { tone: "warn", text: `Connected to ${status.ssid}, but it is not letting the box through yet.` };
  const l = lines(status, wans);
  const signal = status.signal ? `, ${signalWord(status.signal)} signal` : "";
  const use = !l.wifi ? "" : l.count === 1 ? " It is the only internet line this box has up."
    : ` It is one of ${l.count} internet lines this box has up.`;
  return { tone: "good", text: `Connected to ${status.ssid}${signal}.${use}` };
}

/* Why the box could not join, in words, from what NetworkManager said. */
export function joinFailure(error = "") {
  if (/secrets were required|psk|password/i.test(error)) return "the password was not accepted.";
  if (/no network with ssid/i.test(error)) return "the box cannot see that network now.";
  if (/no answer within/i.test(error)) return "the network did not let the box in within 45 seconds.";
  if (/ip configuration|dhcp/i.test(error)) return "the network let the box in but gave it no address.";
  return `the box said: ${error.replace(/^wifi: joining "[^"]*": /, "")}`;
}

/* The latest join's failure, while the box is not on that network. */
export function failedJoin(status) {
  const j = status.join;
  if (!j || j.joining || !j.error || (status.connected && status.ssid === j.ssid)) return null;
  const at = new Date(j.at);
  const when = Number.isNaN(at.getTime()) ? "" : ` at ${at.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
  return `Could not join ${j.ssid}${when}: ${joinFailure(j.error)}`;
}

/* What each action asks before it acts. Leaving the only line up says that
   first, and the button turns red. */
export function confirmDisconnect(ssid, only, singleEdge) {
  return {
    title: `Disconnect from ${ssid}?`,
    message: only
      ? onlyLineWords(ssid, "until it restarts, which joins it again", singleEdge)
      : `The box leaves ${ssid} now, and joins it again by itself when it restarts. Forget it instead to stop that.`,
    confirmLabel: "Disconnect",
    variant: only ? "danger" : "primary",
  };
}

export function confirmForget(ssid, only, singleEdge) {
  const forget = `The box leaves ${ssid} and deletes its saved password, so it never joins it again by itself.`;
  return {
    title: `Forget ${ssid}?`,
    message: only ? `${onlyLineWords(ssid, "until someone at the site connects it again", singleEdge)} ${forget}` : forget,
    confirmLabel: "Forget it",
    variant: "danger",
  };
}

export function confirmSwitch(from, to, only, singleEdge) {
  return {
    title: `Join ${to}?`,
    message: only
      ? `${onlyLineWords(from, "until it is on a network again", singleEdge)} The box leaves ${from} to join ${to}.`
      : `The box leaves ${from} to join ${to}.`,
    confirmLabel: "Join",
    variant: only ? "danger" : "primary",
  };
}
